// src/components/ProductoForm.js
import React, { useEffect, useState } from "react";
import { crearProducto, actualizarProducto } from "../services/productoService";
import Toast, { useToast } from "./Toast";
// Si tienes estilos:
// import "./ProductoForm.css";

// Normaliza números tipo "2,065.29" o "2065,29" -> 2065.29
const toPrecio = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v == null) return 0;
  let s = String(v).trim();
  if (!s) return 0;
  s = s.replace(/\s+/g, "");
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  let decSep = null;
  if (lastComma !== -1 || lastDot !== -1) decSep = lastComma > lastDot ? "," : ".";
  if (decSep) {
    const other = decSep === "," ? /\./g : /,/g;
    s = s.replace(other, ""); // quita miles
    if (decSep === ",") s = s.replace(",", "."); // decimal a punto
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export default function ProductoForm({ empresaId, producto, onClose, onSaved }) {
  const [form, setForm] = useState({
    id: null,
    clave: "",
    descripcion: "",
    unidadMedida: "PZA.",
    precioUnitario: 0,
    claveSAT: "",
    claveUnidad: "",
    existencia: 0,
    estatus: "A", // "A"=Activo, "I"=Inactivo
    stock: false,
  });

  const [saving, setSaving] = useState(false);
  const { toasts, closeToast, error: toastError, warning: toastWarning } = useToast();

  useEffect(() => {
    if (producto) {
      setForm({
        id: producto.id ?? producto.ID ?? producto.Id ?? null,
        clave: producto.clave ?? producto.Clave ?? "",
        descripcion: producto.descripcion ?? producto.Descripcion ?? "",
        unidadMedida: producto.unidadMedida ?? producto.UnidadMedida ?? "PZA.",
        // intenta ambos nombres al leer
        precioUnitario: Number(
          producto.precioUnitario ?? producto.precio_Unitario ?? 0
        ),
        claveSAT: producto.claveSAT ?? producto.ClaveSAT ?? "",
        claveUnidad: producto.claveUnidad ?? producto.ClaveUnidad ?? "",
        existencia: Number(producto.existencia ?? 0),
        estatus: producto.estatus ?? producto.Estatus ?? "A",
        stock: Boolean(producto.stock ?? false),
      });
    }
  }, [producto]);

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (name === "claveSAT") {
      // sólo números y máximo 8 dígitos
      value = value.replace(/\D/g, "").slice(0, 8);
    }
    if (name === "claveUnidad" || name === "unidadMedida" || name === "clave") {
      value = String(value).toUpperCase();
    }

    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };


  const validate = () => {
    if (!form.clave?.trim()) {
      toastWarning("La clave (SKU) es obligatoria.");
      return false;
    }
    if (!form.descripcion?.trim()) {
      toastWarning("La descripción es obligatoria.");
      return false;
    }
    const precio = toPrecio(form.precioUnitario);
    if (precio < 0) {
      toastWarning("El precio unitario no puede ser negativo.");
      return false;
    }
    const sat = (form.claveSAT || "").trim();
    if (!/^\d{8}$/.test(sat)) {
      toastWarning("La Clave SAT es obligatoria y debe tener 8 dígitos.");
      return false;
    }
    const cu = (form.claveUnidad || "").trim().toUpperCase();
    if (!/^[A-Z0-9]{2,5}$/.test(cu)) {
      toastWarning("La Clave Unidad es obligatoria (2 a 5 caracteres alfanuméricos).");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const precio = toPrecio(form.precioUnitario);

    const payload = {
      id: form.id || 0,
      empresa: empresaId,
      clave: form.clave?.trim(),
      descripcion: form.descripcion?.trim(),
      unidadMedida: form.unidadMedida?.trim() || "PZA.",
      // MANDAR AMBAS CLAVES PARA SER 100% COMPATIBLE
      precio_Unitario: precio,
      precioUnitario: precio,
      claveSAT: form.claveSAT.trim(),
      claveUnidad: form.claveUnidad.trim().toUpperCase(),
      existencia: Number.isFinite(+form.existencia) ? +form.existencia : 0,
      estatus: form.estatus,
      stock: !!form.stock,
    };

    try {
      setSaving(true);
      console.log("Payload que se envía:", payload); // <-- para verificar en consola
      if (form.id) {
        await actualizarProducto(form.id, payload, empresaId);
      } else {
        payload.id = 0;
        await crearProducto(payload, empresaId);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("Error al guardar el artículo:", err);
      toastError(err?.response?.data ?? "No se pudo guardar el artículo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Toast toasts={toasts} onClose={closeToast} />
    <form className="producto-form" onSubmit={handleSubmit}>
      <div className="section">
        <div className="section-title">{form.id ? "Editar Producto" : "Agregar Producto"}</div>

        <div className="grid">
          {/* Clave */}
          <div className="field-group" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Clave (SKU)</label>
            <input
              name="clave"
              className="input"
              value={form.clave}
              onChange={handleChange}
              placeholder="DS-K1T34SFMXB/5"
            />
          </div>

          {/* Descripción */}
          <div className="field-group" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Descripción</label>
            <input
              name="descripcion"
              className="input"
              value={form.descripcion}
              onChange={handleChange}
              placeholder="Terminal Min Moe WiFi / Touch..."
            />
          </div>

          {/* Precio Unitario */}
          <div className="field-group">
            <label className="label">Precio Unitario</label>
            <input
              name="precioUnitario"
              type="text" /* text para permitir , o . y miles; lo normalizamos */
              className="input"
              value={form.precioUnitario}
              onChange={handleChange}
              placeholder="2065.29"
            />
          </div>

          {/* Clave SAT */}
          <div className="field-group">
            <label className="label">Clave SAT</label>
            <input
              name="claveSAT"
              className="input"
              value={form.claveSAT}
              onChange={handleChange}
              placeholder="81111508"
              required
              inputMode="numeric"
              pattern="\d{8}"
              title="8 dígitos (p. ej. 81111508)"
            />
          </div>

          {/* Clave Unidad */}
          <div className="field-group">
            <label className="label">Clave Unidad</label>
            <input
              name="claveUnidad"
              className="input"
              value={form.claveUnidad}
              onChange={handleChange}
              placeholder="H87"
              required
              pattern="[A-Za-z0-9]{2,5}"
              title="2 a 5 caracteres alfanuméricos (p. ej. H87, E48)"
            />
          </div>

          {/* Unidad Medida */}
          <div className="field-group">
            <label className="label">Unidad Medida</label>
            <input
              name="unidadMedida"
              className="input"
              value={form.unidadMedida}
              onChange={handleChange}
              placeholder="PZA."
            />
          </div>

          {/* Stock */}
          <div className="field-group">
            <label className="label">Stock</label>
            <input
              name="stock"
              type="checkbox"
              checked={form.stock}
              onChange={handleChange}
              style={{ width: 18, height: 18 }}
            />
          </div>

          {/* Existencia */}
          <div className="field-group">
            <label className="label">Existencia</label>
            <input
              name="existencia"
              className="input"
              type="number"
              min="0"
              value={form.existencia}
              onChange={handleChange}
            />
          </div>

          {/* Estatus */}
          <div className="field-group" style={{ gridColumn: "1 / -1" }}>
            <label className="label">Estatus</label>
            <select
              name="estatus"
              className="input"
              value={form.estatus}
              onChange={handleChange}
            >
              <option value="A">Activo</option>
              <option value="I">Inactivo</option>
            </select>
          </div>
        </div>

        <button className="submit-button" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </form>
    </>
  );
}
